import { MAX_CHAR_LENGTH } from 'common/constants/general';
import { largeCurrencyFormat, smallCurrencyFormat } from 'common/formats/formats';
import { Cells } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { EQUITY_ALLOCATION_KEY } from 'pages/ValuationsAllocation/common/constants/equityAllocation/equityAllocation';
import {
  RANGE_SENSITIVITY_SPREADSHEET_COMPANY_SECTION_HEADER,
  RANGE_SENSITIVITY_SPREADSHEET_FUND_ROW,
  RANGE_SENSITIVITY_SPREADSHEET_FUND_SECTION_HEADER,
  RANGE_SENSITIVITY_SPREADSHEET_HEADER,
  RANGE_SENSITIVITY_SPREADSHEET_SHARE_SECTION_HEADER,
} from 'pages/ValuationsAllocation/common/constants/rangeSensitivity/sheetAliases';
import { getExpression, getObjectValue, getStringValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { CellParserParams, RangeSensitivityParserParams } from './types';
import { RANGE_SENSITIVITY_COLUMN_HEADERS } from '../../constants';

// All section and entity header rows — cells rendered as empty/read-only strings
const SECTION_HEADER_ALIASES = new Set([
  RANGE_SENSITIVITY_SPREADSHEET_COMPANY_SECTION_HEADER,
  RANGE_SENSITIVITY_SPREADSHEET_FUND_SECTION_HEADER,
  RANGE_SENSITIVITY_SPREADSHEET_SHARE_SECTION_HEADER,
]);

const isSectionOrEntityHeader = (alias?: string) => {
  if (!alias) {
    return false;
  }
  return SECTION_HEADER_ALIASES.has(alias) || alias.startsWith('RANGE_SENSITIVITY_ENTITY_HEADER_');
};

const FUND_ROW_ALIAS_PREFIX = `${RANGE_SENSITIVITY_SPREADSHEET_FUND_ROW}_`;

// Column layout (3 data columns):
//   0 (A) = Lower Bound
//   1 (B) = Mid Point
//   2 (C) = Upper Bound

const cellParser = (params: CellParserParams) => {
  const {
    alphabet,
    colIndex,
    column,
    companyId,
    defaultEquityAllocationSheetKey,
    pctCellRef,
    row,
    rowIndex,
  } = params;

  const rowNumber = rowIndex + 1;
  const columnLegend = alphabet[colIndex];
  const cellKey = columnLegend + rowNumber;

  const { value: columnValue } = getObjectValue(column[row.alias!]);

  const { className: rowClassName, ...rowWithoutClassName } = row;
  const cell = { ...rowWithoutClassName, key: cellKey, value: columnValue };

  const parsedCell = { ...cell } as ICell;

  if (row.alias === RANGE_SENSITIVITY_SPREADSHEET_HEADER) {
    parsedCell.value = RANGE_SENSITIVITY_COLUMN_HEADERS[colIndex] ?? '';
    parsedCell.gridType = 'string';
    parsedCell.readOnly = true;
  } else if (isSectionOrEntityHeader(row.alias)) {
    parsedCell.value = '';
    parsedCell.gridType = 'string';
    parsedCell.readOnly = true;
    // Entity header rows: empty data cells only
    if (!row.alias?.startsWith('RANGE_SENSITIVITY_ENTITY_HEADER_')) {
      parsedCell.className = 'bold-row division-top-only';
    }
  } else {
    // Data rows: number formatting with bound expressions
    const rowData = getObjectValue(row.data) as {
      equityAllocationSheetKey?: string;
      isRangeSensitivityShareSecurityRow?: boolean;
    };
    const isShareSecurityRow = Boolean(rowData?.isRangeSensitivityShareSecurityRow);
    const currencyFormat = isShareSecurityRow ? smallCurrencyFormat : largeCurrencyFormat;

    // Mid Point is col B (index 1); pct comes from the companion pct sheet via cross-sheet ref
    const concludedValueColLegend = alphabet[1];
    const concludedCellKey = concludedValueColLegend + rowNumber;

    const lowerBoundExpr = `=${concludedCellKey}*(1-${pctCellRef}/100)`;
    const upperBoundExpr = `=${concludedCellKey}*(1+${pctCellRef}/100)`;

    switch (colIndex) {
      case 0: // Lower Bound
        parsedCell.format = currencyFormat;
        parsedCell.gridType = 'number';
        parsedCell.readOnly = true;
        parsedCell.expr = lowerBoundExpr;
        break;

      case 1: {
        // Mid Point (Concluded Value)
        parsedCell.format = currencyFormat;
        parsedCell.gridType = 'number';
        parsedCell.readOnly = true;
        if (rowData?.isRangeSensitivityShareSecurityRow && row.customKey) {
          const sheetKey =
            getStringValue(rowData.equityAllocationSheetKey) || defaultEquityAllocationSheetKey;
          parsedCell.expr = `=${sheetKey}.${row.customKey}`;
        } else if (row.alias?.startsWith(FUND_ROW_ALIAS_PREFIX) && companyId) {
          const fundId = row.alias.slice(FUND_ROW_ALIAS_PREFIX.length);
          const wsvSheetKey = `weightedShareValues_${companyId}`;
          parsedCell.expr = `=${wsvSheetKey}.TOTAL_${fundId}_VALUE`;
        }
        break;
      }

      case 2: // Upper Bound
        parsedCell.format = currencyFormat;
        parsedCell.gridType = 'number';
        parsedCell.readOnly = true;
        parsedCell.expr = upperBoundExpr;
        break;

      default:
        break;
    }
  }

  // Set default cell values
  const {
    allowEmptyValues = true,
    customKey = null,
    dbType = 'string',
    expr = '',
    format = null,
    gridType = 'string',
    isTextArea = false,
    maxNumberChars = MAX_CHAR_LENGTH,
    value = '',
  } = getObjectValue(parsedCell);

  return {
    [parsedCell.key as string]: {
      ...parsedCell,
      allowEmptyValues,
      columnLegend,
      customKey,
      dbType,
      expr: getExpression({ expr: expr as string | null | undefined, columnLegend }),
      format,
      gridType,
      isTextArea,
      maxNumberChars,
      value: value ?? '',
    } as ICell,
  };
};

const customParser = (params: RangeSensitivityParserParams) => {
  const { columns, rowConfig, tableData } = params;

  let cells = {} as Cells;
  const alphabet = alphabetGenerator([], columns.length);

  const defaultEquityAllocationSheetKey =
    getStringValue(tableData?.equityAllocationSheetKey) || EQUITY_ALLOCATION_KEY;

  // Cross-sheet reference to the companion pct sheet cell
  const pctCellRef = tableData?.pctSheetKey ? `${tableData.pctSheetKey}.A2` : 'A2';

  rowConfig.forEach((row, rowIndex: number) => {
    columns.forEach((column, colIndex: number) => {
      cells = {
        ...cells,
        ...cellParser({
          alphabet,
          colIndex,
          column,
          companyId: tableData?.companyId,
          defaultEquityAllocationSheetKey,
          pctCellRef,
          row,
          rowIndex,
        }),
      };
    });
  });

  return cells;
};

export default customParser;
